import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { signIn } from "../services/authService.js";
import { HttpError } from "../services/http.js";

/**
 * Signing in with a password, from wherever the form happens to be.
 *
 * # Why this is a hook and not repeated in two pages
 *
 * Phase 20a put a sign-in form on the front door as well as on `/login`, and
 * the interesting half of signing in is not the two fields — it is
 * [`message`], the mapping from the server's refusal to a sentence. That
 * mapping is **ADR 0010 in code**: a wrong address and a wrong password come
 * back as one code so the form cannot be used to enumerate accounts, and
 * `account_pending` may be specific only because it is reachable only with the
 * right password.
 *
 * Copied into a second page, that reasoning drifts the first time somebody adds
 * a case to one copy — and the drift is a *disclosure*, not a typo. So the
 * pages own the layout and this owns the rule.
 *
 * A hook rather than a component because [`components/`] may not import
 * `services/` at all (`eslint.config.ts` enforces it): the form takes a
 * callback, and this is what the callback is.
 */
export interface SignIn {
  /** Try it. Navigates to the destination on success; sets [`SignIn::error`] otherwise. */
  submit: (email: string, password: string) => void;
  /** A request is in flight — disable the button, not the fields. */
  busy: boolean;
  /** The server's refusal in words, or `null`. */
  error: string | null;
}

/**
 * @param destination Where to land once signed in. Defaults to the front door.
 *
 * A parameter because of the party link. Somebody handed `…/party/BWDR` who is
 * not signed in is sent to `/login?code=BWDR` (see `JoinPanel`), and landing
 * them on the front door afterwards would mean the code they were given is gone
 * and they have to find it again — at a party, from a chat thread, while a round
 * is running. Since ADR 0027 every player holds an account, so this is not the
 * rare path; it is the **normal** one.
 */
export function useSignIn(destination = "/"): SignIn {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (email: string, password: string) => {
    setBusy(true);
    setError(null);

    void (async () => {
      try {
        await signIn({ email: email.trim(), password });
        // `replace`, not push: Back from the party should not return to a
        // sign-in form for a session that now exists.
        void navigate(destination, { replace: true });
      } catch (cause) {
        setError(message(cause, t));
        setBusy(false);
      }
    })();
  };

  return { submit, busy, error };
}

/**
 * The server's refusal, in words — and no more precise than the server was.
 *
 * `t` is threaded in rather than the helper being a hook: it is a pure mapping
 * from a code to a sentence, and keeping it outside the component is what lets
 * the exhaustive `switch` stay readable.
 */
function message(cause: unknown, t: TFunction): string {
  if (cause instanceof HttpError) {
    switch (cause.code) {
      case "invalid_credentials":
        return t("login.error.invalidCredentials");
      case "account_locked":
        return t("login.error.locked");
      // Phase 19a. Only reachable with the right password, so these may say
      // plainly what is happening — the person reading them owns the account.
      case "account_pending":
        return t("login.error.pending");
      case "account_rejected":
        return t("login.error.rejected");
      case "rate_limited":
        return t("login.error.rateLimited");
      default:
        return cause.message;
    }
  }
  return cause instanceof Error ? cause.message : t("login.error.failed");
}
